import { MAX_PHOTOS, NOTES_MAX_LENGTH, PART_SERIAL_NUMBER_MAX_LENGTH } from '../../constants';
import { getUnitCount } from '../../helpers/getUnitCount';
import { useEffect, useRef, useState } from 'react';
import { isDateAfter, isDateValid } from 'utils/dateTimeUtils/dateValidationUtil';

import { getDroneIntervals } from 'pages/MaintenancePage/helpers/getDroneIntervals';

import type {
    CalibrationKind,
    OnAddPhotos,
    OnRemovePhoto,
    PartCondition,
    PartType,
    ServiceDraft,
    ServiceEventType,
    ServiceRecordDraft,
    ServiceRecordPhoto,
    UnitSelection,
    UseServiceDraftParams,
    UseServiceDraftReturn
} from './types';

const getInitialDraft = (): ServiceDraft => ({
    notes: '',
    partCondition: 'new',
    partSerialNumber: '',
    performedAt: new Date(),
    photos: [],
    unitSelection: { kind: 'unchosen' }
});

const useServiceDraft = ({
    drone,
    isOpen,
    serviceIntervals
}: UseServiceDraftParams): UseServiceDraftReturn => {
    const [draft, setDraft] = useState<ServiceDraft>(getInitialDraft);

    // Object URLs that made it into a saved record — history thumbnails still render
    // them, so they must survive the draft teardown below.
    const savedPhotoUrlsRef = useRef<Set<string>>(new Set());
    const photosRef = useRef<ServiceRecordPhoto[]>([]);

    useEffect(() => {
        photosRef.current = draft.photos;
    }, [draft.photos]);

    // Reseed whenever the panel opens so a reopened form never shows a stale draft.
    // The discarded draft (cancelled or abandoned) releases its photo object URLs;
    // photos committed to a saved record keep theirs.
    useEffect(() => {
        if (!isOpen) {
            return;
        }

        photosRef.current
            .filter(photo => !savedPhotoUrlsRef.current.has(photo.url))
            .forEach(photo => URL.revokeObjectURL(photo.url));
        setDraft(getInitialDraft());
    }, [isOpen]);

    // The interval configured for the drafted part on this drone's model — gates the
    // used-part condition (SR-23) and anchors its remaining-life range.
    const interval =
        drone && draft.partType
            ? getDroneIntervals({ drone, serviceIntervals })[draft.partType]
            : undefined;

    // Switching the event type resets the target fields — a part picked for a
    // replacement must not leak into a calibration record.
    const handleChangeEventType = ({ eventType }: { eventType: ServiceEventType }) =>
        setDraft(current => ({
            eventType,
            notes: current.notes,
            partCondition: 'new',
            partSerialNumber: '',
            performedAt: current.performedAt,
            photos: current.photos,
            unitSelection: { kind: 'unchosen' }
        }));

    // A new part choice resets the condition too — remaining life is meaningless
    // against a different part's interval, and a serial belongs to the old part.
    // Re-selecting the current part toggles it off (uniform across event types):
    // that is an inspection's path back to "general", and the same dependent
    // clears apply so no stale unit/serial/condition survives the deselection.
    const handleChangePart = ({ partType }: { partType: PartType }) =>
        setDraft(current => ({
            ...current,
            partCondition: 'new',
            partSerialNumber: '',
            partType: current.partType === partType ? undefined : partType,
            remainingLife: undefined,
            unitSelection: { kind: 'unchosen' }
        }));

    const handleChangeCalibrationKind = ({
        calibrationKind
    }: {
        calibrationKind: CalibrationKind;
    }) => setDraft(current => ({ ...current, calibrationKind }));

    // A serial identifies one physical part, so any change of target drops the
    // entered serial; only re-picking the same specific unit keeps it.
    const handleChangeUnit = ({ unitSelection }: { unitSelection: UnitSelection }) =>
        setDraft(current => ({
            ...current,
            partSerialNumber:
                unitSelection.kind === 'unit' &&
                current.unitSelection.kind === 'unit' &&
                unitSelection.unitNumber === current.unitSelection.unitNumber
                    ? current.partSerialNumber
                    : '',
            unitSelection
        }));

    // Choosing 'used' pre-fills remaining life with the full interval so the operator
    // adjusts down from a sensible default; back to 'new' clears the adjustment.
    const handleChangePartCondition = ({ partCondition }: { partCondition: PartCondition }) =>
        setDraft(current => ({
            ...current,
            partCondition,
            remainingLife: partCondition === 'used' ? interval?.value : undefined
        }));

    const handleChangeRemainingLife = ({ remainingLife }: { remainingLife: number }) =>
        setDraft(current => ({ ...current, remainingLife }));

    // Serial entry never blocks a save; the field has no maxLength of its own, so
    // this slice IS the length cap, not a backstop.
    const handleChangePartSerialNumber = ({ partSerialNumber }: { partSerialNumber: string }) =>
        setDraft(current => ({
            ...current,
            partSerialNumber: partSerialNumber.slice(0, PART_SERIAL_NUMBER_MAX_LENGTH)
        }));

    const handleChangePerformedAt = ({ date }: { date?: Date }) =>
        setDraft(current => ({ ...current, performedAt: date }));

    const handleChangeNotes = ({ notes }: { notes: string }) =>
        setDraft(current => ({ ...current, notes }));

    // The field slices selections to the remaining slots; the cap here keeps the draft
    // honest even if a caller skips that guard.
    const handleAddPhotos: OnAddPhotos = ({ photos }) =>
        setDraft(current => ({
            ...current,
            photos: [...current.photos, ...photos].slice(0, MAX_PHOTOS)
        }));

    // A removed photo can no longer reach a saved record, so its object URL is safe
    // to release immediately (the saved-set guard covers any already-committed URL).
    const handleRemovePhoto: OnRemovePhoto = ({ photoId }) => {
        const removedPhoto = photosRef.current.find(photo => photo.id === photoId);

        if (removedPhoto && !savedPhotoUrlsRef.current.has(removedPhoto.url)) {
            URL.revokeObjectURL(removedPhoto.url);
        }

        setDraft(current => ({
            ...current,
            photos: current.photos.filter(photo => photo.id !== photoId)
        }));
    };

    // Called when the draft is committed to a record so the next reseed keeps its
    // photo URLs alive for the history thumbnails.
    const handleDraftSaved = () => {
        draft.photos.forEach(photo => savedPhotoUrlsRef.current.add(photo.url));
    };

    // A part with more than one unit on this drone needs an explicit unit choice —
    // specific or 'All units' — before the record can identify what was worked on.
    const requiresUnitChoice =
        drone !== null &&
        draft.partType !== undefined &&
        getUnitCount({ drone, partType: draft.partType }) > 1;

    const isPartTargetChosen = () =>
        draft.partType !== undefined &&
        (!requiresUnitChoice || draft.unitSelection.kind !== 'unchosen');

    // An inspection's part is optional: with none chosen it saves as a general
    // inspection; once a part is picked the target must resolve like any
    // part-targeted event (unit choice included).
    const targetValidators: Record<ServiceEventType, () => boolean> = {
        calibrated: () => draft.calibrationKind !== undefined,
        inspected: () => draft.partType === undefined || isPartTargetChosen(),
        replaced: isPartTargetChosen,
        serviced: isPartTargetChosen
    };

    const isTargetValid = draft.eventType ? targetValidators[draft.eventType]() : false;
    const isPerformedAtValid =
        draft.performedAt !== undefined &&
        isDateValid(draft.performedAt) &&
        !isDateAfter(draft.performedAt, new Date());
    const areNotesValid = draft.notes.length <= NOTES_MAX_LENGTH;

    // SR-23 — a used part's remaining life must sit inside [0, interval]; the rule
    // only applies to replacements on a part with a configured interval.
    const isRemainingLifeValid =
        draft.eventType !== 'replaced' ||
        draft.partCondition !== 'used' ||
        interval === undefined ||
        (draft.remainingLife !== undefined &&
            draft.remainingLife >= 0 &&
            draft.remainingLife <= interval.value);

    const isDraftValid =
        isTargetValid && isPerformedAtValid && areNotesValid && isRemainingLifeValid;

    const buildDraft = (): ServiceRecordDraft | null => {
        const {
            calibrationKind,
            eventType,
            notes,
            partCondition,
            partSerialNumber,
            partType,
            performedAt,
            photos,
            remainingLife,
            unitSelection
        } = draft;

        if (!isDraftValid || !eventType || !performedAt) {
            return null;
        }

        // The wire shape keeps its optional unitNumber: a specific unit carries its
        // number; an explicit all-units choice (and single-unit parts) omits it.
        const unitNumber = unitSelection.kind === 'unit' ? unitSelection.unitNumber : undefined;

        // SR-23 — the wire field is usage already accrued at install: interval minus
        // remaining life. Present only for used-part replacements; absent means new.
        const initialUsage =
            eventType === 'replaced' &&
            partCondition === 'used' &&
            interval &&
            remainingLife !== undefined
                ? interval.value - remainingLife
                : undefined;

        // The operator-entered serial identifies one physical part; trim-to-undefined
        // keeps blank entries off the wire. The all-units guard re-derives the
        // one-serial-cannot-describe-every-unit invariant inside buildDraft, matching
        // how initialUsage re-derives its precondition above.
        const trimmedSerialNumber =
            unitSelection.kind === 'allUnits' ? undefined : partSerialNumber.trim() || undefined;

        const base = {
            notes: notes.trim() || undefined,
            performedAt: performedAt.toISOString(),
            photos: photos.length > 0 ? photos : undefined
        };

        if (eventType === 'calibrated') {
            return calibrationKind ? { ...base, calibrationKind, eventType } : null;
        }

        if (eventType === 'inspected') {
            // An inspection is general (whole-aircraft, no target fields) unless
            // the operator picked a part; a part-targeted inspection then carries
            // the same target identity as replaced/serviced — part, unit, and the
            // serial verified on the inspected unit.
            return partType
                ? {
                      ...base,
                      eventType,
                      partSerialNumber: trimmedSerialNumber,
                      partType,
                      unitNumber
                  }
                : { ...base, eventType };
        }

        return partType
            ? {
                  ...base,
                  eventType,
                  initialUsage,
                  partSerialNumber: trimmedSerialNumber,
                  partType,
                  unitNumber
              }
            : null;
    };

    return {
        buildDraft,
        draft,
        handleAddPhotos,
        handleChangeCalibrationKind,
        handleChangeEventType,
        handleChangeNotes,
        handleChangePart,
        handleChangePartCondition,
        handleChangePartSerialNumber,
        handleChangePerformedAt,
        handleChangeRemainingLife,
        handleChangeUnit,
        handleDraftSaved,
        handleRemovePhoto,
        isDraftValid,
        isRemainingLifeValid
    };
};

export default useServiceDraft;
